import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SearchNormal1, Refresh2 } from "iconsax-react";
import { Select, Input, SearchableSelect } from "@/shared/ui";
import type { AssetType, ItemStatus, InventorySubtype } from "../types";

export interface AssetTypeOption {
  label: string;
  value: AssetType;
}

export interface StatusOption {
  label: string;
  value: ItemStatus;
}

export interface SubtypeOption {
  label: string;
  value: NonNullable<InventorySubtype>;
}

interface VendorOption {
  id: number;
  name: string;
}

interface Props {
  search: string;
  onSearchChange: (v: string) => void;
  assetTypeFilter: AssetType | null;
  onAssetTypeChange: (v: AssetType | null) => void;
  assetTypeOptions: AssetTypeOption[];
  statusFilter: ItemStatus | null;
  onStatusChange: (v: ItemStatus | null) => void;
  statusOptions: StatusOption[];
  subtypeFilter: NonNullable<InventorySubtype> | null;
  onSubtypeChange: (v: NonNullable<InventorySubtype> | null) => void;
  subtypeOptions: SubtypeOption[];
  vendorFilter: number | null;
  onVendorChange: (v: number | null) => void;
  vendorOptions: VendorOption[];
  hasActiveFilters: boolean;
  onReset: () => void;
}

export default function WarehouseFilters({
  search,
  onSearchChange,
  assetTypeFilter,
  onAssetTypeChange,
  assetTypeOptions,
  statusFilter,
  onStatusChange,
  statusOptions,
  subtypeFilter,
  onSubtypeChange,
  subtypeOptions,
  vendorFilter,
  onVendorChange,
  vendorOptions,
  hasActiveFilters,
  onReset,
}: Props) {
  const { t } = useTranslation("WarehousePage");

  const selectedVendor = useMemo(() => {
    if (vendorFilter === null) return null;
    return vendorOptions.find((v) => v.id === vendorFilter) || null;
  }, [vendorFilter, vendorOptions]);

  return (
    <div className="flex items-center justify-between gap-2 mb-7">
      <div className="flex items-center gap-2">
        <div className="min-w-48">
          <Input
            isSmall
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t("filters.searchPlaceholder")}
            icon={<SearchNormal1 size={14} color="currentColor" />}
          />
        </div>

        {assetTypeOptions.length > 0 && (
          <div className="min-w-40">
            <Select<AssetType>
              options={assetTypeOptions}
              placeholder={t("filters.category")}
              value={assetTypeFilter}
              onChange={onAssetTypeChange}
              isSmall
              width="w-60"
            />
          </div>
        )}

        <div className="min-w-40">
          <Select<ItemStatus>
            options={statusOptions}
            placeholder={t("filters.status")}
            value={statusFilter}
            onChange={onStatusChange}
            isSmall
            width="w-48"
          />
        </div>

        {subtypeOptions.length > 0 && (
          <div className="min-w-40">
            <Select<NonNullable<InventorySubtype>>
              options={subtypeOptions}
              placeholder={t("filters.subtype")}
              value={subtypeFilter}
              onChange={onSubtypeChange}
              isSmall
              width="w-52"
            />
          </div>
        )}

        <div className="min-w-40">
          <SearchableSelect<VendorOption>
            options={vendorOptions}
            placeholder={t("filters.vendor")}
            value={selectedVendor}
            onChange={(v) => onVendorChange(v?.id ?? null)}
            searchKeys={["name"]}
            displayKey="name"
            isSmall
          />
        </div>

        {hasActiveFilters && (
          <button
            className="px-3 h-8 flex justify-center items-center gap-2 content-base-low cursor-pointer"
            onClick={onReset}>
            <Refresh2 size={16} color="currentColor" />
            <span className="text-label-sm">{t("filters.reset")}</span>
          </button>
        )}
      </div>

    </div>
  );
}
